import { useState } from "react";
import {
  createMentorNote,
  updateMentorNote,
  deleteMentorNote,
} from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { Textarea } from "../client/components/ui/textarea";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { Pencil, Trash2, Plus } from "lucide-react";

interface ExistingNote {
  id: string;
  content: string;
}

interface MentorNoteEditorProps {
  studentId: string;
  existingNote?: ExistingNote;
  sessionId?: string;
  tradeId?: string;
  isGlobalNote?: boolean;
  onSaved?: () => void;
}

export default function MentorNoteEditor({
  studentId,
  existingNote,
  sessionId,
  tradeId,
  isGlobalNote,
  onSaved,
}: MentorNoteEditorProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const [content, setContent] = useState(existingNote?.content ?? "");
  const [isEditing, setIsEditing] = useState(!existingNote);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) return;
    setIsSaving(true);
    try {
      if (existingNote) {
        await updateMentorNote({ noteId: existingNote.id, content });
      } else {
        await createMentorNote({ studentId, content, sessionId, tradeId, isGlobalNote });
        setContent("");
      }
      setIsEditing(false);
      onSaved?.();
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingNote) return;
    setIsSaving(true);
    try {
      await deleteMentorNote({ noteId: existingNote.id });
      onSaved?.();
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing && existingNote) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
        <p className="text-foreground whitespace-pre-wrap">{existingNote.content}</p>
        <div className="flex gap-2 mt-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-3 w-3 mr-1" />
            {t.mentor.editNote}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={isSaving}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            {t.mentor.deleteNote}
          </Button>
        </div>
      </div>
    );
  }

  if (!isEditing && !existingNote) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-primary hover:text-primary/80"
        onClick={() => setIsEditing(true)}
      >
        <Plus className="h-3 w-3 mr-1" />
        {t.mentor.addNote}
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t.mentor.notePlaceholder}
        className="min-h-[80px] text-sm resize-none"
        autoFocus
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={handleSave}
          disabled={isSaving || !content.trim()}
        >
          {t.mentor.saveNote}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={() => {
            setIsEditing(false);
            setContent(existingNote?.content ?? "");
          }}
          disabled={isSaving}
        >
          ✕
        </Button>
      </div>
    </div>
  );
}
